import { StateCreator } from "zustand";
import { CARD_SLOT, CARD_TYPE_TO_CARD_SLOT } from "@/features/cards/shared/card-routing/card-config";
import { INFO_CARD_TYPE } from "@/features/cards/shared/card-routing/card-types";
import type { CardEntityType } from "@/services/analytics/events";
import { ANALYTICS_EVENT, trackEvent } from "@/services/analytics/events";
import type MapEntityBase from "../services/map-management/map-entities/map-entity-base";
import { CurrentSlice } from "./current-slice";

export interface CardSlotState {
    isOpen: boolean;
    cardType: INFO_CARD_TYPE | undefined;
    height: number;
    width: number;
    /** When set, the device card opens this inner panel. Cleared when the card slot closes. */
    initialPanelKey: string | undefined;
    /** Analytics: when the card was opened (epoch ms). Cleared on close. */
    openedAtMs: number | undefined;
    /** Analytics: entity selected when the card opened. Cleared on close. */
    entityId: string | undefined;
    entityType: CardEntityType | undefined;
}

const makeDefaultSlot = (): CardSlotState => ({
    isOpen: false,
    cardType: undefined,
    height: 0,
    width: 0,
    initialPanelKey: undefined,
    openedAtMs: undefined,
    entityId: undefined,
    entityType: undefined,
});

const cardTypeToEntityType = (cardType: INFO_CARD_TYPE): CardEntityType | undefined => {
    if (cardType === INFO_CARD_TYPE.DEVICE) return "device";
    if (cardType === INFO_CARD_TYPE.ZONE) return "zone";
    if (cardType === INFO_CARD_TYPE.AOI) return "aoi";
    return undefined;
};

const resolveEntityAnalytics = (
    entity: MapEntityBase | null | undefined,
    cardType: INFO_CARD_TYPE,
): { entityId: string | undefined; entityType: CardEntityType | undefined } => {
    const entityId = entity?.model?.id;
    if (!entityId) {
        return { entityId: undefined, entityType: undefined };
    }
    return { entityId, entityType: cardTypeToEntityType(cardType) };
};

/** Creates a open-card slot state for the given card type */
const createOpenCardSlotState = (
    prev: CardSlotState,
    cardType: INFO_CARD_TYPE,
    initialPanelKey?: string,
    entity?: MapEntityBase | null,
): CardSlotState => {
    const { entityId, entityType } = resolveEntityAnalytics(entity, cardType);
    return {
        ...prev,
        isOpen: true,
        cardType,
        // Always reset measured dimensions on open — a stale height from a previous viewport size
        // (e.g. after window resize or device rotation) causes wrong pan offsets. The fallback in
        // computeEffectiveRightCardDimensions gives a correct estimate until the card is measured.
        height: 0,
        width: 0,
        initialPanelKey,
        openedAtMs: Date.now(),
        entityId,
        entityType,
    };
};

const trackCardClose = (slotState: CardSlotState, cardType: INFO_CARD_TYPE | undefined) => {
    if (!cardType) return;
    trackEvent(ANALYTICS_EVENT.CARD_CLOSE, {
        card_type: cardType,
        entity_id: slotState.entityId,
        entity_type: slotState.entityType,
        open_duration_ms: slotState.openedAtMs !== undefined ? Date.now() - slotState.openedAtMs : undefined,
    });
};

export interface LayoutSlice {
    chipsHeight: number;
    setChipsHeight: (height: number) => void;

    slots: Record<CARD_SLOT, CardSlotState>;

    // `entity` allows null because non-entity cards (e.g. weather) open with no map entity attached.
    openCard: (args: { entity?: MapEntityBase | null; cardType: INFO_CARD_TYPE; initialPanelKey?: string }) => void;
    closeCard: (slot: CARD_SLOT) => void;
    toggleCard: (cardType: INFO_CARD_TYPE) => void;
    setCardDimensions: (slot: CARD_SLOT, height: number, width: number) => void;

    /** Legacy escape hatch — keep until SWD-1343 lands */
    closeCardAndSelect: (entity: MapEntityBase | null, cardType: INFO_CARD_TYPE | undefined) => void;
}

export const createLayoutSlice: StateCreator<LayoutSlice & CurrentSlice, [], [], LayoutSlice> = (set, get) => ({
    chipsHeight: 0,
    setChipsHeight: (height: number) => set({ chipsHeight: height }),
    slots: {
        [CARD_SLOT.BOTTOM_LEFT]: makeDefaultSlot(),
        [CARD_SLOT.BOTTOM_RIGHT]: makeDefaultSlot(),
    },
    setCardDimensions: (slot: CARD_SLOT, height: number, width: number) => {
        set((state) => ({
            slots: {
                ...state.slots,
                [slot]: { ...state.slots[slot], height, width },
            },
        }));
    },
    openCard: ({
        entity,
        cardType,
        initialPanelKey,
    }: {
        entity?: MapEntityBase | null;
        cardType: INFO_CARD_TYPE;
        initialPanelKey?: string;
    }) => {
        const cardSlot = CARD_TYPE_TO_CARD_SLOT[cardType];
        const previousSlot = get().slots[cardSlot];
        // Shared slots (e.g. bottom-right) can be overwritten — close the prior card first so
        // open/close pairs and open_duration_ms stay consistent.
        if (previousSlot.isOpen && previousSlot.cardType) {
            trackCardClose(previousSlot, previousSlot.cardType);
        }
        const { entityId, entityType } = resolveEntityAnalytics(entity, cardType);
        set((state) => ({
            slots: {
                ...state.slots,
                [cardSlot]: createOpenCardSlotState(state.slots[cardSlot], cardType, initialPanelKey, entity),
            },
        }));
        trackEvent(ANALYTICS_EVENT.CARD_OPEN, {
            card_type: cardType,
            entity_id: entityId,
            entity_type: entityType,
        });
        if (entity) {
            get().setSelectedMapEntity(entity);
        }
    },
    closeCard: (slot: CARD_SLOT) => {
        const slotState = get().slots[slot];
        const cardType = slotState.cardType;
        set((state) => ({
            slots: {
                ...state.slots,
                // Reset to DEFAULT_SLOT so stale height/width don't persist after close
                [slot]: makeDefaultSlot(),
            },
        }));
        trackCardClose(slotState, cardType);
    },
    toggleCard: (cardType: INFO_CARD_TYPE) => {
        const cardSlot = CARD_TYPE_TO_CARD_SLOT[cardType];
        const previousSlot = get().slots[cardSlot];
        const willBeOpen = !previousSlot.isOpen;

        set((state) => ({
            slots: {
                ...state.slots,
                [cardSlot]: willBeOpen ? createOpenCardSlotState(state.slots[cardSlot], cardType) : makeDefaultSlot(),
            },
        }));

        if (willBeOpen) {
            trackEvent(ANALYTICS_EVENT.CARD_OPEN, {
                card_type: cardType,
            });
        } else {
            trackCardClose(previousSlot, previousSlot.cardType);
        }
    },
    closeCardAndSelect: (entity: MapEntityBase | null, cardType: INFO_CARD_TYPE | undefined) => {
        /**
         * If no cardType provided, likely a legacy (non-Zustand) card is taking over so we close all open card slots
         * @ToDo this is a legacy fallback and should be removed once we've migrated all legacy cards to Zustand
         */
        if (cardType === undefined) {
            const previousSlots = get().slots;
            set((state) => ({
                slots: Object.fromEntries(Object.keys(state.slots).map((slot) => [slot, makeDefaultSlot()])) as Record<
                    CARD_SLOT,
                    CardSlotState
                >,
            }));
            Object.values(previousSlots).forEach((slotState) => {
                if (slotState.isOpen && slotState.cardType) {
                    trackCardClose(slotState, slotState.cardType);
                }
            });
        } else {
            const cardSlot = CARD_TYPE_TO_CARD_SLOT[cardType];
            if (cardSlot) {
                const previousSlot = get().slots[cardSlot];
                set((state) => ({
                    slots: {
                        ...state.slots,
                        // Reset to DEFAULT_SLOT so stale height/width don't persist after close
                        [cardSlot]: makeDefaultSlot(),
                    },
                }));
                // cardType is the incoming selection — label the close with the card that was open.
                if (previousSlot.isOpen && previousSlot.cardType) {
                    trackCardClose(previousSlot, previousSlot.cardType);
                }
            } else {
                console.warn(`closeCardAndSelect: cardType ${cardType} not found in CARD_TYPE_TO_CARD_SLOT`);
            }
        }

        // Pass through the provided entity
        get().setSelectedMapEntity(entity);
    },
});
